import React, { useEffect, useState } from "react";
import AhaSessionModulesField from "../../../components/aha/AhaSessionModulesField";
import { addStandardCourseItem, deleteStandardCourseItem, getAhaCatalog, updateStandardCourseItem } from "../../../api/backend";
import { Modal, Notice } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { EmptyNote, PanelHeader, errorText, sessionTitle } from "./common";

const hhmm = (t) => (t ? String(t).slice(0, 5) : "");
const EMPTY = { sessionType: "PHYSICAL", startTime: "09:00", endTime: "12:00", ahaModuleKeys: [] };
const ROW = { display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderBottom: "1px solid var(--ga-line)", flexWrap: "wrap" };
const ICON = { width: 34, height: 34, borderRadius: 8, display: "grid", placeItems: "center", background: "var(--ga-soft)", color: "var(--ga-sub)", flexShrink: 0 };

/**
 * The sessions of a standard course: type, times and, for AHA, the modules taught. There are no dates, rooms or
 * locations here: each training center decides those when it schedules the class.
 */
export default function SessionsPanel({ draft, courseId, onChanged }) {
  const sessions = draft.sessions;
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [catalog, setCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const productKey = draft.version.ahaProductKey;
  const issuesAha = (draft.version.certificateKinds || []).includes("AHA");

  useEffect(() => {
    if (!issuesAha || !productKey || catalog) return;
    getAhaCatalog().then(setCatalog).catch(() => setCatalog({ products: [] }));
  }, [issuesAha, productKey, catalog]);

  const modules = (catalog?.products || []).find((p) => p.key === productKey)?.modules || [];

  // A session that teaches AHA modules is titled by them instead of "Session N".
  const titleOf = (s, i) => {
    const names = modules.filter((m) => (s.ahaModuleKeys || []).includes(m.key)).map((m) => m.name);
    return names.length ? names.join(", ") : sessionTitle(s, i);
  };

  const open = (session) => {
    setError(null);
    setForm(session ? { sessionType: session.sessionType, startTime: hhmm(session.startTime), endTime: hhmm(session.endTime), ahaModuleKeys: session.ahaModuleKeys || [] } : EMPTY);
    setEditing(session || {});
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { ...form, startTime: form.startTime || null, endTime: form.endTime || null };
      if (editing.id) await updateStandardCourseItem(courseId, "sessions", editing.id, body);
      else await addStandardCourseItem(courseId, "sessions", body);
      setEditing(null);
      onChanged();
    } catch (e) {
      setError(errorText(e, "The session could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (session) => {
    setError(null);
    try {
      await deleteStandardCourseItem(courseId, "sessions", session.id);
      onChanged();
    } catch (e) {
      setError(errorText(e, "The session could not be removed."));
    }
  };

  return (
    <div className="panel" data-testid="sessions-panel">
      <PanelHeader title="Sessions" subtitle="Dates, rooms and instructors are set by each training center when it schedules the class."
        action={<button type="button" className="b pri sm" onClick={() => open(null)}><I.plus s={12} />Add session</button>} />
      <div className="panel-b" style={{ padding: 0 }}>
        {error && !editing && (
          <div style={{ padding: "12px 18px 0" }}>
            <Notice kind="bad">
              {error}{" "}
              <button type="button" className="ib" aria-label="Close" style={{ width: 24, height: 24, verticalAlign: "middle" }} onClick={() => setError(null)}><I.x s={14} /></button>
            </Notice>
          </div>
        )}
        {sessions.length === 0 ? <EmptyNote>No sessions yet. A course needs at least one.</EmptyNote> : (
          sessions.map((s, i) => (
            <div key={s.id} data-testid={`session-${s.id}`} style={ROW}>
              <span style={ICON}><I.clock s={16} /></span>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700 }}>{titleOf(s, i)}</div>
                <div className="sub" style={{ fontSize: 12.5 }}>
                  {s.sessionType.toLowerCase()} · {s.startTime && s.endTime ? `${hhmm(s.startTime)}-${hhmm(s.endTime)}` : "no times set"}
                  {s.ahaModuleKeys?.length ? ` · modules: ${s.ahaModuleKeys.join(", ")}` : ""}
                </div>
              </div>
              <button type="button" className="b sec sm" onClick={() => open(s)}><I.pencil s={12} />Edit</button>
              <button type="button" className="ib" aria-label={`Remove ${titleOf(s, i)}`} onClick={() => remove(s)}><I.x s={16} /></button>
            </div>
          ))
        )}
      </div>

      <Modal
        open={Boolean(editing)}
        onClose={() => !saving && setEditing(null)}
        title={editing?.id ? "Edit session" : "Add session"}
        labelId="session-dialog-title"
        footer={(
          <>
            <button type="button" className="b ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
            <button type="button" className="b pri" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save session"}</button>
          </>
        )}
      >
        <label className="field">Type
          <select className="sel" value={form.sessionType} onChange={(e) => setForm((f) => ({ ...f, sessionType: e.target.value }))}>
            {["PHYSICAL", "VIRTUAL", "HYBRID"].map((t) => <option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</option>)}
          </select>
        </label>
        <div className="g2">
          <label className="field">Start<span className="inp"><input type="time" value={form.startTime} onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))} /></span></label>
          <label className="field">End<span className="inp"><input type="time" value={form.endTime} onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))} /></span></label>
        </div>
        {issuesAha && modules.length > 0 && (
          <AhaSessionModulesField modules={modules} value={form.ahaModuleKeys} onChange={(keys) => setForm((f) => ({ ...f, ahaModuleKeys: keys }))} />
        )}
        {issuesAha && !productKey && <div className="sub" style={{ fontSize: 13 }}>Choose the AHA product on the Details tab to pick this session's modules.</div>}
        {error && <Notice kind="bad">{error}</Notice>}
      </Modal>
    </div>
  );
}
